import { useCallback, useEffect, useState } from "react";

export type AppearanceDensity = "comfortable" | "compact";
export type AppearanceTextSize = "sm" | "md" | "lg" | "xl";
export type AppearanceContrast = "standard" | "high";
export type AppearanceMotion = "auto" | "full" | "reduced";
export type AppearanceFont = "default" | "legible" | "system";
export type AppearanceMotionFeel = "smooth" | "spring" | "sharp" | "off";
export type AppearanceCardSize = "compact" | "normal" | "large";
export type AppearanceTickerSpeed = "slow" | "medium" | "fast";
export type AppearanceTickerCount = "few" | "normal" | "many";
export type AppearanceBorderColor =
  | "default"
  | "none"
  | "lavender"
  | "salmon"
  | "red"
  | "earth"
  | "gold"
  | "teal";

export interface Appearance {
  density: AppearanceDensity;
  textSize: AppearanceTextSize;
  contrast: AppearanceContrast;
  motion: AppearanceMotion;
  font: AppearanceFont;
  motionFeel: AppearanceMotionFeel;
  cardSize: AppearanceCardSize;
  tickerSpeed: AppearanceTickerSpeed;
  tickerCount: AppearanceTickerCount;
  borderColor: AppearanceBorderColor;
}

export const STORAGE_KEY = "assure-appearance";

export const DEFAULT_APPEARANCE: Appearance = {
  density: "comfortable",
  textSize: "md",
  contrast: "standard",
  motion: "auto",
  font: "default",
  motionFeel: "smooth",
  cardSize: "normal",
  tickerSpeed: "medium",
  tickerCount: "normal",
  borderColor: "default",
};

const DENSITIES = [
  "comfortable",
  "compact",
] as const satisfies readonly AppearanceDensity[];
const TEXT_SIZES = [
  "sm",
  "md",
  "lg",
  "xl",
] as const satisfies readonly AppearanceTextSize[];
const CONTRASTS = [
  "standard",
  "high",
] as const satisfies readonly AppearanceContrast[];
const MOTIONS = [
  "auto",
  "full",
  "reduced",
] as const satisfies readonly AppearanceMotion[];
const FONTS = [
  "default",
  "legible",
  "system",
] as const satisfies readonly AppearanceFont[];
const MOTION_FEELS = [
  "smooth",
  "spring",
  "sharp",
  "off",
] as const satisfies readonly AppearanceMotionFeel[];
const CARD_SIZES = [
  "compact",
  "normal",
  "large",
] as const satisfies readonly AppearanceCardSize[];
const TICKER_SPEEDS = [
  "slow",
  "medium",
  "fast",
] as const satisfies readonly AppearanceTickerSpeed[];
const TICKER_COUNTS = [
  "few",
  "normal",
  "many",
] as const satisfies readonly AppearanceTickerCount[];
const BORDER_COLORS = [
  "default",
  "none",
  "lavender",
  "salmon",
  "red",
  "earth",
  "gold",
  "teal",
] as const satisfies readonly AppearanceBorderColor[];

function includes<T extends string>(
  allowed: readonly T[],
  value: unknown,
): value is T {
  return (
    typeof value === "string" && (allowed as readonly string[]).includes(value)
  );
}

export function isAppearance(value: unknown): value is Appearance {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    includes(DENSITIES, candidate.density) &&
    includes(TEXT_SIZES, candidate.textSize) &&
    includes(CONTRASTS, candidate.contrast) &&
    includes(MOTIONS, candidate.motion) &&
    includes(FONTS, candidate.font) &&
    includes(MOTION_FEELS, candidate.motionFeel) &&
    includes(CARD_SIZES, candidate.cardSize) &&
    includes(TICKER_SPEEDS, candidate.tickerSpeed) &&
    includes(TICKER_COUNTS, candidate.tickerCount) &&
    includes(BORDER_COLORS, candidate.borderColor)
  );
}

export function parseAppearance(raw: string | null): Appearance {
  if (!raw) return DEFAULT_APPEARANCE;
  try {
    const value: unknown = JSON.parse(raw);
    if (value === null || typeof value !== "object") return DEFAULT_APPEARANCE;
    const migrated = {
      ...DEFAULT_APPEARANCE,
      ...(value as Partial<Appearance>),
    };
    return isAppearance(migrated) ? migrated : DEFAULT_APPEARANCE;
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export function applyAppearance(doc: Document, appearance: Appearance): void {
  const root = doc.documentElement;
  root.setAttribute("data-density", appearance.density);
  root.setAttribute("data-text-size", appearance.textSize);
  root.setAttribute("data-contrast", appearance.contrast);
  root.setAttribute("data-motion", appearance.motion);
  root.setAttribute("data-reading-font", appearance.font);
  root.setAttribute("data-motion-feel", appearance.motionFeel);
  root.setAttribute("data-card-size", appearance.cardSize);
  root.setAttribute("data-ticker-speed", appearance.tickerSpeed);
  root.setAttribute("data-ticker-count", appearance.tickerCount);
  root.setAttribute("data-border-color", appearance.borderColor);
}

function readStoredAppearance(): Appearance {
  try {
    if (typeof window === "undefined") return DEFAULT_APPEARANCE;
    return parseAppearance(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

function persistAppearance(appearance: Appearance): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(appearance));
  } catch {
    // The in-memory appearance still applies when storage is unavailable.
  }
}

export function applyStoredAppearance(doc: Document = document): Appearance {
  const appearance = readStoredAppearance();
  applyAppearance(doc, appearance);
  return appearance;
}

export function useAppearance(): {
  appearance: Appearance;
  setAppearance: (appearance: Appearance) => void;
} {
  const [appearance, setAppearanceState] =
    useState<Appearance>(readStoredAppearance);

  useEffect(() => {
    applyAppearance(document, appearance);
  }, [appearance]);

  const setAppearance = useCallback((next: Appearance) => {
    setAppearanceState(next);
    persistAppearance(next);
  }, []);

  return { appearance, setAppearance };
}
